import { SHEET_KEYS } from '../../../models/sheet-keys';
import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import {
  pickRole22Schema,
  Role22Schema,
  Role22SchemaControl,
} from './role22-notes-list-of-notes.rules';

/**
 * Pure validator for Role 22 (Notes - List of Notes).
 *
 * Mirrors the editor's role22-730000-notes-list-of-notes.component.ts
 * validation surface. The component's `getValidators()` currently returns
 * an empty array for every control (the sheet is a comprehensive list of
 * optional TextBlock disclosures, explicitly allowed to persist even while
 * "invalid" — see the component's setupStatePersistence() comment), and
 * neither schema (MFRS/MPERS) carries any `required`, `validators`, or
 * `requiredIfCrossSheet` metadata today.
 *
 * The validator is still written schema-driven so that if a future schema
 * revision ever marks a disclosure required — or introduces a cross-sheet
 * requirement — the pure path and the mount-based path will light up
 * together. Following the role 20 template also keeps the lift mechanical
 * and makes the registry entry uniform across notes sheets.
 *
 * ---- Schema variant handling ----------------------------------------------
 *
 * Role 22 has TWO form JSONs (role-730000-mfrs.form.json with ~138 MFRS
 * disclosures and role-730000-mpers.form.json with ~83 MPERS disclosures)
 * that differ in qname prefix AND in the set of declared controls. The
 * component fetches the right one at mount time based on
 * filingPrerequisites['ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements'].
 *
 * The pure path gets the same value through context.accountingStandard
 * (assembled by ConversionValidatorService.buildContext from filingInfo —
 * which is the same DEI field). No change to the SheetValidator interface
 * is required; selection is delegated to pickRole22Schema so the component
 * and validator stay DRY.
 */

interface SchemaWithControls {
  controls: ReadonlyArray<Role22SchemaControl>;
}

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * Mirrors the component's getValidators() check: a control is statically
 * required when `required === true` OR when its validators array contains
 * an entry with `name === 'required'`. Either source produces the same
 * Validators.required push so we treat them identically.
 */
function isStaticallyRequired(control: Role22SchemaControl): boolean {
  if (control.required === true) return true;
  return Array.isArray(control.validators)
    ? control.validators.some((validator) => validator?.name === 'required')
    : false;
}

function checkStaticRequiredErrors(
  schema: SchemaWithControls,
  sheetData: Record<string, any>
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const control of schema.controls) {
    if (isStaticallyRequired(control) && isEmpty(sheetData[control.qname])) {
      errors[control.qname] = `${control.qname} is required`;
    }
  }
  return errors;
}

/**
 * Reads the referenced sheet's field value out of context.crossSheetData.
 * When the orchestrator didn't include the referenced sheet in the bundle
 * (e.g. the dependency map missed it) we return undefined, which mirrors
 * the component's pre-subscription behaviour: the requirement silently
 * degrades to "not required" and the field is not flagged.
 */
function readCrossSheetFieldValue(
  context: SheetValidationContext,
  sheet: string,
  field: string
): unknown {
  const bundle = context.crossSheetData;
  if (!bundle) return undefined;
  const sheetPayload = bundle.get(sheet as Parameters<typeof bundle.get>[0]);
  if (!sheetPayload) return undefined;
  return sheetPayload[field];
}

function checkCrossSheetRequiredErrors(
  schema: SchemaWithControls,
  sheetData: Record<string, any>,
  context: SheetValidationContext
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const control of schema.controls) {
    const rule = control.requiredIfCrossSheet;
    if (!rule) continue;

    const refValue = readCrossSheetFieldValue(context, rule.sheet, rule.field);
    const shouldBeRequired = refValue === rule.equals;
    if (shouldBeRequired && isEmpty(sheetData[control.qname])) {
      errors[control.qname] = `${control.qname} is required`;
    }
  }
  return errors;
}

export const role22NotesListOfNotesValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.notesListOfNotes,
  // Tier 2: schema-variant selection via context.accountingStandard is the
  // sheet's only non-trivial dependency beyond its own payload. No
  // calculations, no row repetition, no cross-sheet reads in the current
  // schema (the requiredIfCrossSheet branch is kept for schema-driven
  // future-proofing — see rules file). Classified Tier 2 alongside the
  // sibling notes-corporate-information variant-driven sheet.
  tier: 2,
  validate(
    sheetData: Record<string, any>,
    context: SheetValidationContext
  ): PureSheetValidationResult {
    const schema = pickRole22Schema(context.accountingStandard) as unknown as SchemaWithControls & Role22Schema;

    const errors: Record<string, string> = {
      ...checkStaticRequiredErrors(schema, sheetData),
      ...checkCrossSheetRequiredErrors(schema, sheetData, context),
    };

    return {
      hasErrors: Object.keys(errors).length > 0,
      hasSuggestedChanges: false,
      errors,
    };
  },
};
